import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/src/lib/database.types';

export const NOTE_FILES_BUCKET = 'note-files';

// Allowed file types for note attachments
export const ALLOWED_NOTE_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/quicktime',
  'audio/mpeg',
  'audio/mp3',
  'audio/ogg',
] as const;

export const ALLOWED_NOTE_FILE_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.mp4',
  '.mov',
  '.mp3',
  '.ogg',
] as const;

export const MAX_NOTE_FILE_SIZE = 25 * 1024 * 1024; // 25MB (to accommodate WhatsApp media)

/**
 * Generate a UUID using browser's crypto API
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Build storage path for note file
 * Pattern: {userId}/{clientId}/{uuid}.{extension}
 */
export function buildNoteFilePath(params: {
  userId: string;
  clientId: string;
  originalName?: string | null;
}) {
  const safeName = params.originalName?.replace(/\s+/g, '-').toLowerCase();
  const extension = safeName?.split('.').pop();
  const fileName = extension
    ? `${generateUUID()}.${extension}`
    : `${generateUUID()}`;
  return `${params.userId}/${params.clientId}/${fileName}`;
}

/**
 * Validate file type for note attachments
 */
export function isValidNoteFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > MAX_NOTE_FILE_SIZE) {
    return {
      valid: false,
      error: `File size exceeds 25MB limit. File: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    };
  }

  // Check file type
  const isValidType = ALLOWED_NOTE_FILE_TYPES.includes(file.type as any);
  const hasValidExtension = ALLOWED_NOTE_FILE_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );

  if (!isValidType && !hasValidExtension) {
    return {
      valid: false,
      error: 'Invalid file type. Allowed: PDF, DOC, DOCX, TXT, images (JPG, PNG), videos (MP4), and audio (MP3).',
    };
  }

  return { valid: true };
}

/**
 * Upload note file to storage
 */
export async function uploadNoteFile(
  supabase: SupabaseClient<Database>,
  path: string,
  file: File,
) {
  // Validate file before upload
  const validation = isValidNoteFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const buffer = Buffer.from(await file.arrayBuffer());
  const contentType = file.type || 'application/octet-stream';

  const { error } = await supabase.storage
    .from(NOTE_FILES_BUCKET)
    .upload(path, buffer, {
      contentType,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return contentType;
}

/**
 * Remove note file from storage
 */
export async function removeNoteFile(
  supabase: SupabaseClient<Database>,
  path?: string | null,
) {
  if (!path) return;
  const { error } = await supabase.storage
    .from(NOTE_FILES_BUCKET)
    .remove([path]);
  if (error) {
    console.error('Failed to remove note file:', error);
  }
}

/**
 * Create signed URL for note file download
 */
export async function createSignedNoteUrl(
  supabase: SupabaseClient<Database>,
  path?: string | null,
  expiresInSeconds = 60 * 15, // 15 minutes default
) {
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage
      .from(NOTE_FILES_BUCKET)
      .createSignedUrl(path, expiresInSeconds);
    if (error) {
      console.error('Unable to generate signed note URL:', error);
      return null;
    }
    return data.signedUrl;
  } catch (error) {
    console.error('Signed URL error:', error);
    return null;
  }
}

/**
 * Get file icon based on file type
 */
export function getFileIcon(fileName: string): string {
  const ext = fileName.toLowerCase().split('.').pop();
  switch (ext) {
    case 'pdf':
      return '📄';
    case 'doc':
    case 'docx':
      return '📋';
    case 'txt':
      return '📝';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'webp':
      return '🖼️';
    case 'mp4':
    case 'mov':
      return '🎥';
    case 'mp3':
    case 'ogg':
      return '🎵';
    default:
      return '📎';
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}
